/**
 * 日建連 GoogleMap 操作用クラス
 * 2025/04/11
 */

import GoogleMapsApiLoader from 'google-maps-api-loader';

export default class
{
  /**
   * コンストラクタ
   * @param {string} [map_id="map"] - The ID of the HTML element where the map will be rendered.
   * @param {number} [zoom=12] - The initial zoom level of the map.
   * @param {object} [clickCallback=null] - クリックイベント後のコールバック関数
   * @param {string} [draggableCursor="crosshair"] - The cursor style when dragging on the map.
   */
  constructor( map_id="map", zoom=12, clickCallback=null, draggableCursor="crosshair" ) {
    this.map_id          = map_id;
    this.zoom            = zoom;
    this.draggableCursor = draggableCursor;
    this.clickCallback   = clickCallback;
    this.maptElement     = null;

    this.google        = null;
    this.map           = null;
    this.clickedMarker = null;
    this.markers       = [];
    this.infoWindows   = [];
  }

  /** Googleマップを初期化する
   */
  async init(mapCenter) {
    const mapOptions = {
      center: mapCenter,
      zoom  : this.zoom,
      mapId : 'nkrmap',
      mapTypeId: "roadmap",  // "roadmap" or "satellite" or "hybrid" or "terrain"
      draggableCursor: this.draggableCursor,
      streetViewControl: true, // ストリートビューボタンを表示
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: "dropdown_menu"  // or "horizontal_bar"
      },
    };

    this.mapElement = document.getElementById(this.map_id);
    if (!this.mapElement) {
      throw new Error(`地図要素（id="${this.map_id}"）が見つかりません`);
    }

    let apiKey = "AIzaSyCMyUDyk1OV70frW1MztPJLmlN0KVX6fco"; // 本番用のAPIキー
    if( location.href.indexOf("www-dev") >= 0 ) {
      // テストサーバー用のAPIキー
      apiKey = "AIzaSyDLKo-KL77gycZJhxaZ6LAilDDqY3PbCx4";
    }
    else if( location.href.indexOf("/www.") < 0 ) {
      // ローカル環境用のAPIキー
      apiKey = "AIzaSyCr_7En7CT6ZpkNe53yCHKak3WDe22tF9Y";
    }

    this.google = await GoogleMapsApiLoader({
      apiKey: apiKey
    });

    this.map = await new this.google.maps.Map(
      this.mapElement, mapOptions
    );

    this.map.addListener('click', (e) => {
      console.log('Map clicked!', e, e.latLng.lat(), e.latLng.lng());
      this.closeClickedMarker();
      this.addMarker(e.latLng.lat(), e.latLng.lng());
    });
  }

  getLatLng(lat, lng) {
    return new this.google.maps.LatLng(lat, lng);
  }

  getLatLngObject(latlng) {
    const t = latlng.split(",").map(v => parseFloat(v));
    return new this.google.maps.LatLng(t[0],t[1]);
  }

  /** 指定した座標を地図の中央にする
   * @param {number} lat - The latitude of the new center.
   * @param {number} lng - The longitude of the new center.
   */
  setMapCenter(lat, lng) {
    this.map.setCenter({lat: lat, lng: lng});
  }

  /** クリックで設置したマーカーをクリアする */
  closeClickedMarker() {
    if (this.clickedMarker) {
      this.clickedMarker.setMap(null);
      this.clickedMarker = null;
    }
    if( this.clickCallback ) {
      this.clickCallback("", "");
    }
  }

  /** 指定座標にマーカーを設置する
   * @async
   * @param {number} lat - The latitude of the marker.
   * @param {number} lng - The longitude of the marker.
   * @returns {Promise<void>}
   */
  async addMarker(lat, lng) {
    const { AdvancedMarkerElement,PinElement } = await this.google.maps.importLibrary("marker");
    const pin = new PinElement({
      borderColor: "#666666",
      background: '#FF9900',
      glyph: "＋",
      glyphColor: "black",
    });
    this.closeClickedMarker();
    this.clickedMarker = new AdvancedMarkerElement({
      position: this.getLatLng(lat,lng),
      map: this.map,
      title: 'Clicked Location',
      content: pin.element,
    });
    this.clickedMarker.addListener('gmp-click', () => {
      console.log('Clicked marker!', this.clickedMarker.position);
      this.closeClickedMarker();
    });
    if( this.clickCallback ) {
      this.clickCallback(lat, lng);
    }
  }

  getSvgMarkerLabel(num) {
    const tag = ( num < 10 )
      ? `<text class="cls-2" transform="translate(45.15 116.79)"><tspan x="0" y="0">${num}</tspan></text>`
      : `<text class="cls-2" transform="translate(16.87 116.79)"><tspan x="0" y="0">${num}</tspan></text>`;

    return `
<?xml version="1.0" encoding="UTF-8"?>
<svg id="marker-layer-2${num}" data-name="marker-layer-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 147.75 153.64">
  <defs>
    <style>
      .cls-1 {
        fill: #fff;
      }

      .cls-2 {
        fill: #231815;
        font-family: Impact, Impact;
        font-size: 111.47px;
      }
    </style>
  </defs>
  <g id="marker-layer-${num}" data-name="marker-layer-${num}">
    <circle class="cls-1" cx="70.88" cy="75.88" r="73.88"/>
    ${tag}
  </g>
</svg>`;
  }

  /** fetchで読み込んだマーカーを設置する
   * @async
   * @param {Array<{loc: google.maps.LatLngLiteral, name: string}>} locs - An array of location objects containing coordinates and names.
   * @returns {Promise<void>}
   */
  async markerSet( locs, showNumber = false ) {
    const { AdvancedMarkerElement,PinElement } = await this.google.maps.importLibrary("marker");
    this.markers = locs.map((location,i) => {

      let backColor = '#00FF00';
      let borderColor = '#66FF33';
      if( location.basic_form_1 ) {
        backColor = '#FF0000';
        borderColor = '#FF6633';
      }
      else if( location.basic_form_2 ) {
        backColor = '#0000FF';
        borderColor = '#3366FF';
      }

      let pin = new PinElement({
          background: backColor,
          borderColor: borderColor,
          glyphColor: 'white',
        });

      if( showNumber ) {
        // SVG要素を作成
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        let n = i+1;
        svg.innerHTML = this.getSvgMarkerLabel(n); // マーカー用画像
        svg.setAttribute("width", "40px");
        svg.setAttribute("height","40px");

        pin = new PinElement({
          glyph: svg, // マーカーの番号を表示
          background: backColor,
          borderColor: borderColor,
          glyphColor: 'white'
        });
      }

      const ll = location.latlng.split(',');

      let mk = new AdvancedMarkerElement({
        position: new this.google.maps.LatLng(parseFloat(ll[0]), parseFloat(ll[1])),
        map: this.map,
        title: 'Clicked Location',
        content: pin.element,
      });
      mk.addListener('gmp-click', () => {
        console.log('Marker clicked!', mk.position, mk.index);
        this.closeAllInfoWindows();
        this.infoWindows[mk.index].open(this.map, mk);
      });

      mk.index = i;

      let box = `${location.address}<br><a href="./pdf/${location.sheet_no}.pdf" class="pdf" target="_blank">PDFダウンロード<span class="pdf_link">&nbsp;</span></a>`;
      let opt = { maxWidth: 500, minWidth: 200 };
      let infowindow = new this.google.maps.InfoWindow({
          headerContent: location.title,
          content: box,
          options: opt,
      });
      infowindow.close();
      this.infoWindows.push(infowindow);

      return mk;
    });
  }

  openInfoWindow(index) {
    if (index < 0 || index >= this.infoWindows.length) {
      console.error("Invalid index for info window:", index);
      return;
    }
    const marker = this.markers[index];
    if (marker) {
      this.closeAllInfoWindows();
      this.infoWindows[index].open(this.map, marker);
    } else {
      console.error("Marker not found for index:", index);
    }
  }

  /** fetchで設置したマーカーを全て削除する */
  async removeMarkers() {
    this.markers.forEach(function(mk) {
      mk.setMap(null);
      mk = null;
    });
    this.markers = [];
  }

  /** 情報ウインドウを全て削除する */
  closeAllInfoWindows() {
    this.infoWindows.forEach((infowindow) => {
      infowindow.close();
    });
  }

  /** マーカー一覧から、緯度経度の幅と中心点を得る */
  getBoundsAndCenter(locations) {
    if (!locations || locations.length === 0) {
      return null;
    }

    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;

    locations.forEach(location => {
      minLat = Math.min(location.lat, minLat);
      maxLat = Math.max(location.lat, maxLat);
      minLng = Math.min(location.lng, minLng);
      maxLng = Math.max(location.lng, maxLng);
    });

    const center = {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2
    };

    return {
      "maxLat":maxLat,
      "minLat":minLat,
      "maxLng":maxLng,
      "minLng":minLng,
      "center"  :center
    };
  }

  /** 緯度経度の幅と中心点から適切なズームと中心点を設定する */
  setMapBoundsAndCenter(boundsData) {
    const bounds = new this.google.maps.LatLngBounds(
      new this.google.maps.LatLng(boundsData.minLat, boundsData.minLng),
      new this.google.maps.LatLng(boundsData.maxLat, boundsData.maxLng)
    );

    const mapDiv = document.getElementById('map');
    const mapW = mapDiv.offsetWidth;
    const mapH = mapDiv.offsetHeight;

    const zoom = this.getBoundsZoomLevel(bounds, mapW, mapH);

    // 地図の中心点とズームを設定
    this.map.setCenter(boundsData.center);
    this.map.setZoom(zoom - 1);
  }

  getBoundsZoomLevel(bounds, mapWidth, mapHeight) {
    const WORLD_DIM = { width: 256, height: 256 };
    const ZOOM_MAX = 14;

    const latRad = (lat) => {
      const sin = Math.sin(lat * Math.PI / 180);
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    };

    const zoom = (mapPx, worldPx, fraction) => {
      return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    };

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    let lngDiff = ne.lng() - sw.lng();
    if (lngDiff < 0) {
      lngDiff += 360;
    }
    const lngFraction = lngDiff / 360;

    const latZoom = zoom(mapHeight, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapWidth, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  }

  // Google Maps API v3で中心点と半径から円を描画する関数
  // center: { lat: 緯度, lng: 経度 }
  // radius: メートル単位
  drawCircle(center, radius, zoom) {
    const circle = new this.google.maps.Circle({
      strokeColor: "#FF0000",   // 円の境界線の色
      strokeOpacity: 0.8,        // 境界線の透明度
      strokeWeight: 2,           // 境界線の太さ
      fillColor: "#FF0000",     // 円の塗りつぶし色
      fillOpacity: 0.35,         // 塗りつぶしの透明度
      map: this.map,                  // 描画する地図インスタンス
      center: center,            // 円の中心座標
      radius: radius             // 半径（メートル単位）
    });

    this.map.setZoom(zoom); // ズームレベルを設定

    return circle;
  }

  removeCircle(circle) {
    if (circle) {
      circle.setMap(null); // 地図から円を取り除く
    }
  }

  // Google Maps API v3で二点間のルートを表示する関数
  // origin, destination: { lat: 緯度, lng: 経度 }
  // type: 移動手段: DRIVING, WALKING, BICYCLING, TRANSIT
  displayRoute(origin, destination, type = "DRIVING") {
    const directionsService = new this.google.maps.DirectionsService();
    const directionsRenderer = new this.google.maps.DirectionsRenderer({
      map: this.map
    });

    const request = {
      origin: origin,
      destination: destination,
      travelMode: type,
      unitSystem: this.google.maps.UnitSystem.METRIC,
    };

    directionsService.route(request, (result, status) => {
      if (status === this.google.maps.DirectionsStatus.OK) {
        directionsRenderer.setDirections(result);
      } else {
        console.error("ルート取得に失敗しました: " + status);
      }
    });

    return directionsRenderer;
  }

  // ルートを削除する関数
  clearRoute(directionsRenderer) {
    if (directionsRenderer) {
      directionsRenderer.setDirections({ routes: [] });
    }
  }

}
